// Redux
import api from '@/redux/redux-api';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { Action, ApiAction, AsyncResult, Result } from '@/redux/index';
import { getMyContacts } from '@/redux/modules/contacts';
import { startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';

// Types
import { Household, Households } from '@/types/households';
import { SortingField } from '@/types/data';
import { ReduxState } from './types';
import { ReducedPortfolioFDC3 } from '@/types/portfolio-fdc3';
import Portfolio from '@/types/portfolio';
import ReducedComparison from '@/types/reduced-comparison';

// Utils
import { transformPortfoliosToFDC3Base } from '@/utils/portfolios';
import { postMessageFilterContactPortfolios, postMessageSortContactPortfolios } from '@/utils/post-messages/post-message-utils';
import { PostMessageSender } from '@/utils/post-messages/outgoing-messages/post-message-sender';

// Constants
const PORTFOLIOS_QUERY_LIMIT = 30;
const COMPARISONS_QUERY_LIMIT = 30;

const initialState = {
  contextHouseholdId: undefined,
  households: [],
  currentHousehold: undefined,
  comparisons: [],
  portfolios: [],
  nextHouseholdComparisonsToken: undefined,
  nextHouseholdPortfoliosToken: undefined,
  hasMore: {
    comparisons: false,
    portfolios: false,
  },
  loading: {
    portfolios: false,
  },
};

// Actions
export const GET_HOUSEHOLDS: Action = {
  type: 'connect/households/get-households',
};

export const GET_HOUSEHOLD: ApiAction = {
  type: 'connect/households/get-by-id',
  endpoint: 'households/:id/',
};

export const GET_HOUSEHOLD_COMPARISONS: ApiAction = {
  type: 'connect/households/get-recent-comparisons',
  endpoint: 'households/:householdId/comparisons',
};

export const GET_HOUSEHOLD_PORTFOLIOS: ApiAction = {
  type: 'connect/households/get-portfolios',
  endpoint: 'households/:householdId/portfolios',
};

export const DELETE_HOUSEHOLD_PORTFOLIOS: Action = {
  type: 'connect/households/delete-portfolio',
};

export const SET_MY_HOUSEHOLDS: Action = {
  type: 'connect/households/set-households',
};

export const CLEAR_CURRENT_HOUSEHOLD: Action = {
  type: 'connect/households/clear-current-household',
};

export const SET_MY_CONTEXT_HOUSEHOLD_ID: Action = {
  type: 'connect/households/set-my-context-household-id',
};

function householdReducer(state: Households = initialState, action: Action) {
  switch (action.type) {
    case `${CLEAR_CURRENT_HOUSEHOLD.type}`: {
      return {
        ...state,
        currentHousehold: undefined,
      };
    }
    case `${GET_HOUSEHOLDS.type}`: {
      // TODO: Households > we will eventually receive either a "householdID" from LWC, or from API call after selecting a household
      // ** The below redux implementation will need to be updated as a result. For now, we are mocking receiving a single household
      return {
        ...state,
        ...{
          households: action.households,
          currentHousehold: action.households[0],
        },
      };
    }
    case `${GET_HOUSEHOLD.type}_SUCCESS`: {
      return {
        ...state,
        currentHousehold: action.payload,
      };
    }
    case `${GET_HOUSEHOLD_COMPARISONS.type}_SUCCESS`: {
      const { comparisons, next } = action.data.data;

      return {
        ...state,
        comparisons: action.params.next && state.comparisons ? state.comparisons.concat(comparisons) : comparisons,
        nextHouseholdComparisonsToken: next,
        hasMore: {
          ...state.hasMore,
          comparisons: !!next,
        },
      };
    }
    case GET_HOUSEHOLD_PORTFOLIOS.type: {
      return {
        ...state,
        loading: {
          ...state.loading,
          portfolios: !action.next,
        },
      };
    }
    case `${GET_HOUSEHOLD_PORTFOLIOS.type}_ERROR`: {
      return {
        ...state,
        portfolios: [],
        nextHouseholdPortfoliosToken: undefined,
        hasMore: {
          ...state.hasMore,
          portfolios: false,
        },
        loading: {
          ...state.loading,
          portfolios: false,
        },
      };
    }
    case `${GET_HOUSEHOLD_PORTFOLIOS.type}_SUCCESS`: {
      const { portfolios, next } = action.data.data;

      return {
        ...state,
        portfolios: action.params.next && state.portfolios ? state.portfolios.concat(portfolios) : portfolios,
        nextHouseholdPortfoliosToken: next,
        hasMore: {
          ...state.hasMore,
          portfolios: !!next,
        },
        loading: {
          ...state.loading,
          portfolios: false,
        },
      };
    }
    case SET_MY_HOUSEHOLDS.type: {
      return {
        ...state,
        households: action.households,
        portfolios: action.portfolios,
      };
    }
    case SET_MY_CONTEXT_HOUSEHOLD_ID.type: {
      return {
        ...state,
        contextHouseholdId: action.contextHouseholdId,
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const clearCurrentHousehold = (): Result<void> => dispatch => {
  dispatch(CLEAR_CURRENT_HOUSEHOLD);
};

const getHouseholdByIdFromCapIntel =
  (id: string): AsyncResult<Household> =>
  (dispatch, getState) => {
    dispatch(startLoaderAction(GET_HOUSEHOLD.type, { id }));
    return api
      .get(GET_HOUSEHOLD, { id })
      .then(async (res: { data: any }) => {
        const household = res.data;
        await dispatch(getMyContacts({ householdId: household.id }));
        const state = getState();
        const result = { ...household, contacts: state.contacts.contacts };
        dispatch({ type: `${GET_HOUSEHOLD.type}_SUCCESS`, payload: result });
        return result;
      })
      .finally(() => {
        dispatch(stopLoaderAction(GET_HOUSEHOLD.type, { id }));
      });
  };

const getHouseholdByIdFromPostMessage = (dispatch: ThunkDispatch<ReduxState, null, AnyAction>, getState: Function, id: string): Promise<Household> => {
  const state = getState();
  const {
    households: { households },
  } = state;

  // Get the household from the store
  const selectedHousehold = households.find((household: Household) => household.id === id);
  dispatch({ type: `${GET_HOUSEHOLD.type}_SUCCESS`, payload: selectedHousehold });
  return Promise.resolve(selectedHousehold);
};

const getHouseholdPortfoliosFromCapIntel =
  (query?: string, sort?: SortingField[], shouldFetchNext = true): AsyncResult<Portfolio[]> =>
  (dispatch, getState) => {
    const state = getState();

    const {
      households: { currentHousehold, nextHouseholdPortfoliosToken },
      intl: { locale },
    } = state;

    const params: any = {
      householdId: currentHousehold?.id,
      locale,
    };

    if (query) {
      params.query = query;
    }

    if (sort && sort.length > 0) {
      params.sort = `${sort[0].id} ${sort[0].order}`;
    }

    if (shouldFetchNext && nextHouseholdPortfoliosToken) {
      params.next = nextHouseholdPortfoliosToken;
      params.limit = PORTFOLIOS_QUERY_LIMIT;
    }

    return api.get(GET_HOUSEHOLD_PORTFOLIOS, params).then((res: { data: any }) => res.data.portfolios);
  };

const getHouseholdPortfoliosFromPostMessage = (
  dispatch: ThunkDispatch<ReduxState, null, AnyAction>,
  getState: () => ReduxState,
  query?: string,
  sort?: SortingField[],
): Promise<Portfolio[]> => {
  const state = getState();
  const {
    contacts: { postMessagePortfolios },
    households: { households, currentHousehold },
    intl: { locale },
    auth: { user },
  } = state;
  dispatch(startLoaderAction(GET_HOUSEHOLD_PORTFOLIOS.type));
  // Loop through all contacts in the current household and collect portfolios that match each contact ID
  const currentHouseholdPortfolios = currentHousehold?.contacts
    ? currentHousehold.contacts.flatMap(contact => postMessagePortfolios.filter((portfolio: Portfolio) => portfolio.contact?._id === contact._id))
    : [];

  const portfoliosWithNoHoldings = currentHouseholdPortfolios.filter((portfolio: Portfolio) => portfolio.holdings.length === 0);
  // if portfolios have no holdings, we need to fetch them from the post message event
  if (portfoliosWithNoHoldings.length > 0) {
    const selectedFDC3BasePortfolios: ReducedPortfolioFDC3[] = transformPortfoliosToFDC3Base(portfoliosWithNoHoldings);
    PostMessageSender.getPortfolios(dispatch, getState, user, selectedFDC3BasePortfolios);
    // No need to return to portfolio here since they will be added to the store in the post message portfolios-strategy
    return Promise.resolve([]);
  }
  // If all portfolios have holdings, we can filter and sort them directly
  let filteredHouseholdPortfolios: Portfolio[] = currentHouseholdPortfolios;

  if (query) {
    filteredHouseholdPortfolios = postMessageFilterContactPortfolios(locale, filteredHouseholdPortfolios, query);
  }

  filteredHouseholdPortfolios = postMessageSortContactPortfolios(locale, filteredHouseholdPortfolios, sort);

  return Promise.resolve(filteredHouseholdPortfolios).finally(() => {
    dispatch({ type: SET_MY_HOUSEHOLDS.type, households, portfolios: filteredHouseholdPortfolios });
    dispatch(stopLoaderAction(GET_HOUSEHOLD_PORTFOLIOS.type));
  });
};

export const getHouseholdById =
  (id: string): AsyncResult<Household> =>
  (dispatch, getState) => {
    const { isPostMessageActive } = getState().postMessage;
    return isPostMessageActive ? getHouseholdByIdFromPostMessage(dispatch, getState, id) : dispatch(getHouseholdByIdFromCapIntel(id));
  };

export const getHouseholdComparisons =
  (query?: string, shouldFetchNext = true): AsyncResult<ReducedComparison[]> =>
  (dispatch, getState) => {
    const state: ReduxState = getState();

    const {
      households: { currentHousehold, nextHouseholdComparisonsToken },
      intl: { locale },
    } = state;

    const params: any = {
      householdId: currentHousehold?.id,
      locale,
      contactIds: currentHousehold?.contacts?.map(contact => contact._id).join(';'),
    };

    if (query) {
      params.q = query;
    }

    if (shouldFetchNext && nextHouseholdComparisonsToken) {
      params.next = nextHouseholdComparisonsToken;
      params.limit = COMPARISONS_QUERY_LIMIT;
    }

    return api.get(GET_HOUSEHOLD_COMPARISONS, params).then((res: { data: any }) => res.data.comparisons);
  };

export const getHouseholdPortfolios =
  (query?: string, sort?: SortingField[], shouldFetchNext = true): AsyncResult<Portfolio[]> =>
  (dispatch, getState) => {
    const { isPostMessageActive } = getState().postMessage;
    return isPostMessageActive
      ? getHouseholdPortfoliosFromPostMessage(dispatch, getState, query, sort)
      : dispatch(getHouseholdPortfoliosFromCapIntel(query, sort, shouldFetchNext));
  };

export const setMyHouseholds =
  (households: Household[], portfolios: Portfolio[]): Result<void> =>
  dispatch => {
    // Households and household portfolios used to update the components
    dispatch({ type: SET_MY_HOUSEHOLDS.type, households, portfolios });
  };

export const setMyContextHouseholdId =
  (contextHouseholdId: string): Result<void> =>
  dispatch => {
    // Set the initial context householdid coming from Advisor Dashboard flow
    dispatch({ type: SET_MY_CONTEXT_HOUSEHOLD_ID.type, contextHouseholdId });
  };

export default householdReducer;
